import DefaultErrorPage from 'next/error'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import {
  Button,
  Collapse,
  Container,
  Navbar,
  Offcanvas,
  Stack,
} from 'react-bootstrap'
import { FaCaretDown, FaCaretLeft, FaCaretRight } from 'react-icons/fa'
import { HeadProps } from 'web_core/assets/Head'
import { IconWrapper } from 'web_core/assets/IconWrapper'
import { GlossaryTitleLink } from 'web_core/assets/glossary'
import { Pagination } from 'web_core/assets/page'
import { LoadingSpinner } from 'web_core/assets/spinners'
import { ActiveContentsText } from 'web_core/assets/table/ActiveContentsText'
import { ContentsTable } from 'web_core/assets/table/ContentsTable'
import { SidebarContentsTable } from 'web_core/assets/table/SidebarContentsTable'
import {
  TextbookChaptersTable,
  TextbookImage,
  TextbookImageWithTitle,
} from 'web_core/assets/textbook'
import { PrintSettingProvider } from 'web_core/context/print_setting'
import { Chapter, Glossary, Page, Textbook } from 'web_core/firebase'
import { useElementSize } from 'web_core/hooks'
import {
  ContentsTableData,
  useContentsTable,
} from 'web_core/hooks/useContentsTable'
import { Designed } from 'web_core/types'
import { DefaultLayout } from '~/components/Layout/Default'
import { useLayoutProperty } from '~/context/layout_property'

type Props = {
  contentRef: React.RefObject<HTMLDivElement>
  isNotFound: boolean
  content: Glossary | Textbook | undefined
  pages?: Page[]
  chapters?: Chapter[]
  currentPage?: Page
  children: React.ReactNode
  head: HeadProps
}

export const ContentLayout = ({
  contentRef,
  isNotFound,
  content,
  pages,
  chapters,
  currentPage,
  children,
  head,
}: Props) => {
  const { headerHeight } = useLayoutProperty()
  const contentsTable = useContentsTable(
    contentRef,
    headerHeight,
    content?.parent_id === 'textbooks' ? currentPage : content
  )
  const currentPageIndex = pages?.findIndex((p) => p.id === currentPage?.id)

  if (isNotFound) return <DefaultErrorPage statusCode={404} />

  return (
    <DefaultLayout
      head={head}
      maxWidth={1300}
      header={{ pc: { fixed: false }, sp: { fixed: false } }}
      padding={{ sp: false, pc: false }}
    >
      <PrintSettingProvider>
        {content ? (
          <>
            <MobileMenu
              contentsTableData={contentsTable}
              content={content}
              className="sticky-top d-lg-none d-print-none"
            />
            <Stack direction="horizontal" className="align-items-stretch gap-5">
              <div className="flex-grow-1 overflow-hidden px-2 px-xl-4 px-md-4">
                {children}
              </div>
              <RightSidebar
                content={content}
                pages={pages}
                chapters={chapters}
                currentPage={currentPage}
                currentPageIndex={currentPageIndex}
                contentsTable={contentsTable}
              />
            </Stack>
            {content.parent_id === 'textbooks' &&
              pages &&
              currentPage &&
              currentPageIndex !== undefined && (
                <MobileBottomMenu
                  textbook={content}
                  pages={pages}
                  chapters={chapters}
                  currentPage={currentPage}
                  currentPageIndex={currentPageIndex}
                />
              )}
          </>
        ) : (
          <LoadingSpinner />
        )}
      </PrintSettingProvider>
    </DefaultLayout>
  )
}

type MobileMenuProps = {
  contentsTableData: ContentsTableData
  content: Glossary | Textbook
}

export const MobileMenu = ({
  contentsTableData,
  content,
  ...designProps
}: Designed<MobileMenuProps>) => {
  const [open, setOpen] = useState(false)
  const { headings, activeIndex } = contentsTableData
  const isLoading = activeIndex === undefined || headings === undefined
  return (
    <div {...designProps}>
      <Navbar className="border-bottom flex-column align-items-start w-100 mw-100 overflow-hidden bg-body-tertiary">
        <Container className="px-2 px-lg-4 gap-2 pe-3" fluid>
          <div className="flex-grow-1 overflow-hidden">
            <Button
              variant="link"
              className="d-flex mw-100 text-muted gap-1"
              onClick={() => {
                if (!isLoading && headings.length > 0) setOpen(!open)
              }}
              aria-expanded={open}
            >
              {!isLoading && headings.length > 0 && (
                <IconWrapper className="flex-shrink-0">
                  <FaCaretDown />
                </IconWrapper>
              )}
              <ActiveContentsText
                data={contentsTableData}
                className="flex-grow-1 text-truncate"
                defaultText={
                  content.parent_id === 'textbooks'
                    ? content.title
                    : content.terms[0]?.display
                }
              />
            </Button>
          </div>
        </Container>
        <Collapse in={open}>
          <div
            className="w-100 overflow-auto mt-2 px-2 px-lg-4"
            style={{ maxHeight: '50vh' }}
          >
            <></>
            <div className="text-break pb-2">
              {content.parent_id === 'textbooks'
                ? content.title
                : content.terms[0]?.display}
            </div>
            <ContentsTable
              data={contentsTableData}
              onClick={(heading) => {
                setOpen(false)
                heading.scroll()
              }}
            />
          </div>
        </Collapse>
      </Navbar>
    </div>
  )
}

type TextbookPageRightSidebarContentsProps = {
  textbook: Textbook
  pages: Page[]
  chapters?: Chapter[]
  currentPage: Page
  currentPageIndex: number
  contentsTable: ContentsTableData
}

const TextbookPageRightSidebarContents = ({
  textbook,
  pages,
  chapters,
  currentPage,
  currentPageIndex,
  contentsTable,
}: TextbookPageRightSidebarContentsProps) => {
  const router = useRouter()
  const headings = contentsTable.headings
  return (
    <>
      <TextbookImageWithTitle textbook={textbook} width={72} />
      {/* 本全体の章立てと、開いているページ内の見出しを1つのスクロール領域にまとめる */}
      <div className="flex-grow-1 overflow-auto pt-3">
        {chapters && (
          <TextbookChaptersTable
            textbook={textbook}
            chapters={chapters}
            pages={pages}
            currentPage={currentPage}
          />
        )}
        {headings !== undefined && headings.length > 0 && (
          <>
            <hr className="my-3" />
            <p className="text-muted mb-1 fs-7">このページの見出し</p>
            <ContentsTable data={contentsTable} />
          </>
        )}
      </div>
      <Pagination
        pages={pages}
        currentPageIndex={currentPageIndex}
        onClickPrev={async () => {
          await router.push(
            `/textbooks/${textbook.id}/${pages[currentPageIndex - 1].id}`
          )
        }}
        onClickNext={async () => {
          await router.push(
            `/textbooks/${textbook.id}/${pages[currentPageIndex + 1].id}`
          )
        }}
        className="d-flex fs-7 text-muted align-items-center justify-content-between mt-4 flex-shrink-0"
      />
    </>
  )
}

type GlossaryRightSidebarContentsProps = {
  glossary: Glossary
  contentsTable: ContentsTableData
}

const GlossaryRightSidebarContents = ({
  glossary,
  contentsTable,
}: GlossaryRightSidebarContentsProps) => (
  <>
    <GlossaryTitleLink glossary={glossary} />
    <SidebarContentsTable
      data={contentsTable}
      className="d-flex flex-column overflow-hidden"
    />
  </>
)

type RightSidebarProps = {
  content: Glossary | Textbook
  pages?: Page[]
  chapters?: Chapter[]
  currentPage?: Page
  currentPageIndex?: number
  contentsTable: ContentsTableData
}

const RightSidebar = ({
  content,
  pages,
  chapters,
  currentPage,
  currentPageIndex,
  contentsTable,
}: RightSidebarProps) => (
  <div className="flex-shrink-0 d-none d-xl-block d-print-none pt-2 pt-xl-4 pe-2 pe-md-4">
    <Link href="/support" passHref>
      <a>
        <Image
          src="/support_banner.png"
          alt="Mathpediaを支援する"
          width={320}
          height={(1276 / 1370) * 320}
        />
      </a>
    </Link>
    <div
      className="sticky-top overflow-hidden d-flex flex-column py-3 dvh-100"
      style={{ width: '18rem' }}
    >
      {content.parent_id === 'glossaries' ? (
        <GlossaryRightSidebarContents
          glossary={content}
          contentsTable={contentsTable}
        />
      ) : pages && currentPage && currentPageIndex !== undefined ? (
        <TextbookPageRightSidebarContents
          textbook={content}
          pages={pages}
          chapters={chapters}
          currentPage={currentPage}
          currentPageIndex={currentPageIndex}
          contentsTable={contentsTable}
        />
      ) : (
        <></>
      )}
    </div>
  </div>
)

type MobileBottomMenuProps = {
  textbook: Textbook
  pages: Page[]
  chapters?: Chapter[]
  currentPage: Page
  currentPageIndex: number
}

const MobileBottomMenu = ({
  textbook,
  pages,
  chapters,
  currentPage,
  currentPageIndex,
}: MobileBottomMenuProps) => {
  const router = useRouter()
  const [show, setShow] = useState(false)
  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)

  const { elementRef: mobileBottomMenuRef, height: mobileBottomMenuHeight } =
    useElementSize()

  type MovePageButtonProps = {
    textbook: Textbook
    pages: Page[]
    currentPageIndex: number
  }

  const PrevButton = ({
    textbook,
    pages,
    currentPageIndex,
    ...wrapperProps
  }: Designed<MovePageButtonProps>) => {
    return currentPageIndex > 0 ? (
      <div
        {...wrapperProps}
        onClick={async () => {
          await router.push(
            `/textbooks/${textbook.id}/${pages[currentPageIndex - 1].id}`
          )
        }}
      >
        <div className="d-flex fs-3 text-muted">
          <FaCaretLeft />
        </div>
        <div className="d-flex fs-7 text-muted">前ページへ</div>
      </div>
    ) : (
      <div {...wrapperProps} />
    )
  }

  const NextButton = ({
    textbook,
    pages,
    currentPageIndex,
    ...wrapperProps
  }: Designed<MovePageButtonProps>) => {
    return currentPageIndex < pages.length - 1 ? (
      <div
        {...wrapperProps}
        onClick={async () => {
          await router.push(
            `/textbooks/${textbook.id}/${pages[currentPageIndex + 1].id}`
          )
        }}
      >
        <div className="d-flex fs-7 text-muted">次ページへ</div>
        <div className="d-flex fs-3 text-muted">
          <FaCaretRight />
        </div>
      </div>
    ) : (
      <div {...wrapperProps} />
    )
  }

  return (
    <>
      <div
        className="d-xl-none bg-light fixed-bottom w-100 border-top shadow-lg d-print-none"
        ref={mobileBottomMenuRef}
      >
        <div className="container">
          <div className="row" style={{ height: 40 }}>
            <PrevButton
              textbook={textbook}
              pages={pages}
              currentPageIndex={currentPageIndex}
              className="col d-flex align-items-center justify-content-center cursor-pointer"
            />
            <div
              className="col d-flex align-items-center justify-content-center cursor-pointer border-start border-end"
              onClick={handleShow}
            >
              <span className="mb-0 fs-7">
                <TextbookImage textbook={textbook} width={25} />
              </span>
            </div>
            <NextButton
              textbook={textbook}
              pages={pages}
              currentPageIndex={currentPageIndex}
              className="col d-flex align-items-center justify-content-center cursor-pointer"
            />
          </div>
        </div>
        <Offcanvas
          show={show}
          onHide={handleClose}
          placement={'bottom'}
          style={{ minHeight: '60svh' }}
          scroll
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className="text-break text-truncate-3 pe-2">
              {textbook.title}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            {chapters ? (
              <TextbookChaptersTable
                textbook={textbook}
                chapters={chapters}
                pages={pages}
                currentPage={currentPage}
                onSelectPage={handleClose}
                className="fs-6"
              />
            ) : (
              <LoadingSpinner />
            )}
          </Offcanvas.Body>
        </Offcanvas>
      </div>
      <Container
        style={{ height: mobileBottomMenuHeight }} // MobileBottomMenu の高さ分
        className="d-xl-none d-print-none"
        fluid
      />
    </>
  )
}
